import { ComponentType } from '@angular/cdk/portal';
import { Component, Input, OnInit, ViewChild, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ReportApprovalStatus } from '@core/enums/report-approval-status';
import { TreeItemStatus } from '@core/enums/tree-item-status';
import { Assignment } from '@core/interfaces/assignment';
import { TaskReviewerInput } from '@core/interfaces/modal-inputs/task-reviewer-input';
import { RejectReportModalInput } from '@core/interfaces/modal-inputs/reject-report-modal-input';
import { MyReportApprovalListItem } from '@core/interfaces/my-report-approval-list-item';
import { ApproveInspectionReportRequest } from '@core/interfaces/requests/approve-inspection-report-request';
import { InspectionRequestApiService } from '@web/inspection-requests/services/inspection-request-api.service';
import { AssignmentService } from '@web/services/assignment.service';
import { HubService } from '@web/services/hub.service';
import { TenantRegistrationService } from '@web/tenant-registration/services/tenant-registration.service';
import { ApproversService } from '@web/services/approvers.service';
import { ApproveReport } from '@core/store/actions/my-items.action';
import { Store } from '@ngrx/store';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { DialogService, SnackBarService } from 'osc-reference-app-common';
import { firstValueFrom } from 'rxjs';
import { AppState } from 'src/app/app.state';
import { ReportComponent } from 'src/app/report-shared/components';
import { LoggerService } from '@shared/services/logger.service';
import { MatButton, MatMiniFabButton } from '@angular/material/button';
import { NgClass } from '@angular/common';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { UserProfile } from '@osc-utils/models/user-profile';
import { SignatureConfigModalComponent } from '@osc-utils/components/signature-config/signature-config-modal.component';
import { ConfigurationService } from '@osc-utils/services/configuration.service';
import { SpinnerService } from '@osc-utils/services/spinner.service';
import { ReportsApproverResponse } from '@core/interfaces/responses/reports-approver-response';
import { CurrentUserService } from '@core/services/current-user.service';
import { RejectReportModalComponent } from '../../dialogs/reject-report-modal/reject-report-modal.component';
import { ReportRoleService } from '../../../report-shared/services/report-role.service';
import { InspectionApprovalService } from '../../services/inspection-approval.service';
import { ReportLayoutUiService } from './report-layout-ui.service';

@Component({
  selector: 'app-report-layout',
  templateUrl: './report-layout.component.html',
  styleUrls: ['./report-layout.component.scss'],
  imports: [MatButton, MatMiniFabButton, MatTooltip, NgClass, MatIcon, ReportComponent, TranslatePipe],
  providers: [InspectionRequestApiService],
})
export class ReportLayoutComponent implements OnInit {
  @Input() public saveReport: boolean;
  @Input() public displaySignRejectButtons: boolean;
  @Input() public projectId: string;
  @Input() public jobId: string;
  @Input() public taskId: string;
  @Input() public inspectionType: string;
  @Input() public data: TaskReviewerInput;
  @ViewChild('report') public reportComponent: ReportComponent;
  public configurationService = inject(ConfigurationService);
  public userProfile: UserProfile;
  public isApprover: boolean;
  public registrationtype: string;
  public approvalItem: MyReportApprovalListItem;
  public isRejectInProgress: boolean;
  public isSaveInProgress: boolean;
  public isApprovalInProgress: boolean;
  public assignments: Assignment[];
  public approver: ReportsApproverResponse;
  public allowApproval = false;
  public isPendingApproval = false;
  public canCurrentUserAOApprove = false;
  public mainSpinnerId = 'ipmInteractiveIsoMapModalId';
  public isShowRejectBtn: boolean;
  public isExternalAssignment = false;
  #translate = inject(TranslateService);
  #dialogService = inject(DialogService);
  #_store = inject<Store<AppState>>(Store);
  #reportRoleService = inject(ReportRoleService);
  #dialog = inject(MatDialog);
  #spinnerService = inject(SpinnerService);
  #approversService = inject(ApproversService);
  #assignmentService = inject(AssignmentService);
  #snackBarService = inject(SnackBarService);
  #hubService = inject(HubService);
  #reportLayoutUiService = inject(ReportLayoutUiService);
  #logger = inject(LoggerService);
  #currentUserService = inject(CurrentUserService);
  #inspectionRequestApiService = inject(InspectionRequestApiService);
  #currentUser: UserProfile;
  #canCurrentUserApprove = false;
  #inspectionApprovalService = inject(InspectionApprovalService);
  readonly #tenantRegistrationService = inject(TenantRegistrationService);

  public get isApproveDisabled(): boolean {
    return !this.isApprover || !this.#canCurrentUserApprove || (this.isExternalAssignment && this.registrationtype === 'AO');
  }

  // Condition repeated throughout the codebase.
  // Want to check if we are in a AO 2TF setup for the IR
  public get isReportReadOnly(): boolean {
    return this.isExternalAssignment && this.registrationtype === 'AO';
  }

  public get isRejectDisabled(): boolean {
    if (this.data.task.status === TreeItemStatus.PendingApproval && this.approvalItem?.status === ReportApprovalStatus.Rejected) {
      return false;
    } else {
      return !this.approvalItem || this.approvalItem?.status === ReportApprovalStatus.Rejected;
    }
  }
  public async ngOnInit(): Promise<void> {
    this.approvalItem = await firstValueFrom(this.#reportRoleService.getCurrentUserApproval(this.taskId));
    this.isApprover = !!this.approvalItem;

    this.#currentUser = await firstValueFrom(this.#currentUserService.currentUser$);

    this.registrationtype = this.#tenantRegistrationService.tenantRegistrationType();

    const project = await firstValueFrom(this.#hubService.getProject(this.projectId));
    this.isShowRejectBtn = this.registrationtype === 'AO' && project.isExternalProject;
    if (project.linkedIRId) {
      const ir = await firstValueFrom(this.#inspectionRequestApiService.getInspectionRequest(project.linkedIRId));
      this.isExternalAssignment = ir.isExternalAssignment ?? false;
    }
    this.assignments = await this.#assignmentService.getProjectAssignments(this.projectId);
    this.reportComponent.previewMode = !this.data.isReportEditable;
    this.approver = await firstValueFrom(this.#approversService.fetchTaskApprover(this.projectId, this.taskId));
    this.#canCurrentUserApprove = await this.VerifyIfCurrentUserCanApprove();
    this.canCurrentUserAOApprove = await this.VerifyIfCurrentUserCanAOApprove();
  }

  public async approveByAO() {
    if (this.data.task.status !== TreeItemStatus.SentForAOApproval) {
      return;
    }

    this.#spinnerService.show(this.#spinnerService.mainSpinnerId, this.#translate.instant('interactiveIsometricMapModal.ngOnInitTimeout'));
    try {
      await firstValueFrom(this.#inspectionApprovalService.complete(this.data.task.hubId, this.data.task.jobId, this.data.task.id));
      this.data.task.status = TreeItemStatus.Completed;
      this.#spinnerService.hide(this.#spinnerService.mainSpinnerId);
      this.canCurrentUserAOApprove = false;
    } catch (err) {
      this.#logger.error('Error approving task', err);
      this.#spinnerService.hide(this.#spinnerService.mainSpinnerId);
    }
  }

  public reject(): void {
    this.isRejectInProgress = true;
    const data: RejectReportModalInput = {
      taskName: this.approvalItem.taskName,
      reportId: this.approvalItem.inspectionReportApprovalId,
      taskId: this.taskId,
      projectId: this.projectId,
    };

    const dialogRef = this.#dialogService.openDialog<RejectReportModalComponent, RejectReportModalInput, string>(
      RejectReportModalComponent as ComponentType<RejectReportModalComponent>,
      data,
    );

    dialogRef.afterClosed().subscribe(res => {
      this.isRejectInProgress = false;
      if (res) {
        this.reportComponent
          .removeSignatureDetailsInTemplate()
          .then(() => this.reportComponent.saveTemplate())
          .then(() => this.reportComponent.addReportHtml());
      }
    });
  }

  public async approve(): Promise<void> {
    this.isApprovalInProgress = true;
    const approvalItem = await firstValueFrom(this.#reportRoleService.getCurrentUserApproval(this.taskId));
    const dialogRef = this.#dialog.open(SignatureConfigModalComponent);
    dialogRef.afterClosed().subscribe(data => {
      if (data) {
        this.reportComponent.previewMode = true;
        this.reportComponent.currentDateTime = new Date().toLocaleString();
        this.reportComponent.autoSaveEnabled = false;
        const currentDateTUC = new Date().toISOString();
        const request: ApproveInspectionReportRequest = {
          inspectionReportApprovalId: approvalItem.inspectionReportApprovalId,
          signatureBase64: data.signatureBase64,
          dateApprovedUtc: currentDateTUC,
          approverId: this.#currentUser.id,
        };
        this.#spinnerService.show(this.#spinnerService.mainSpinnerId, this.#translate.instant('measurementReport.signFailed'));
        this.#inspectionApprovalService
          .approveInspection(request)
          .then(() => this.reportComponent.addSignatureDetailsInTemplate(data.signatureBase64, false, true))
          .then(() => this.reportComponent.saveTemplate())
          .then(() => this.reportComponent.addReportHtml())
          .then(() => {
            if (this.approver?.status === ReportApprovalStatus.Pending || this.approver?.status === ReportApprovalStatus.Rejected) {
              this.#inspectionApprovalService.completeInspectionAfterApproval(this.projectId, this.taskId);
            }
          })
          .then(() => {
            this.isApprovalInProgress = false;
            this.reportComponent.previewMode = false;
            this.#spinnerService.hide(this.#spinnerService.mainSpinnerId);
            this.#snackBarService.success(this.#translate.instant('measurementReport.signSuccessfully'));
            this.reportComponent.isSignSuccessful = true;
            this.#_store.dispatch(ApproveReport({ reportId: approvalItem.inspectionReportApprovalId }));
            this.reportComponent.changeDetectorRef.markForCheck();
          })
          .catch(err => {
            this.#logger.error('Error signing report', err);
            this.isApprovalInProgress = false;
            this.reportComponent.previewMode = false;
            this.#spinnerService.hide(this.#spinnerService.mainSpinnerId);
            this.#snackBarService.error(this.#translate.instant('measurementReport.signWithError'));
          })
          .finally(() => (this.reportComponent.autoSaveEnabled = true));
      } else {
        this.isApprovalInProgress = false;
      }
    });
  }

  public async save(): Promise<void> {
    this.isSaveInProgress = true;
    await this.reportComponent.saveTemplate();
    this.isSaveInProgress = false;
  }

  public generatePdf(): void {
    this.#spinnerService.show(this.mainSpinnerId, this.#translate.instant('interactiveIsometricMapModal.ngOnInitTimeout'));
    this.reportComponent
      .addReportHtml(true)
      .then(() => {
        this.#snackBarService.success(this.#translate.instant('measurementReport.pdfSuccessMsg'));
        this.#spinnerService.hide(this.mainSpinnerId);
      })
      .catch(err => {
        this.#logger.error('Error generating pdf', err);
        this.#snackBarService.error(this.#translate.instant('measurementReport.pdfErrorMsg'));
        this.#spinnerService.hide(this.mainSpinnerId);
      });
  }

  public isReportUpdating(): boolean {
    return this.reportComponent?.getReportUpdateStatus() || false;
  }

  public hasPendingWrites(): boolean {
    return this.reportComponent?.hasPendingWrites() || false;
  }

  private async VerifyIfCurrentUserCanApprove(): Promise<boolean> {
    const isTaskPendingApproval = this.data.task.status === TreeItemStatus.PendingApproval;
    if (!isTaskPendingApproval) {
      return false;
    }
    if (this.approver?.status === ReportApprovalStatus.Rejected) {
      return this.#currentUser.id === this.approver.userId;
    }
    if (this.approver?.status === ReportApprovalStatus.Pending) {
      return this.#currentUser.id === this.approver.userId;
    }
  }

  private async VerifyIfCurrentUserCanAOApprove(): Promise<boolean> {
    if (this.data.task.status === 'SentForAOApproval' && this.registrationtype === 'AO') {
      return await firstValueFrom(this.#reportLayoutUiService.isCurrentUserTaskApprover(this.data.task.hubId));
    }
    return false;
  }
}
